/**
 * Axios Instance Configuration
 * Centralized HTTP client with interceptors for authentication and error handling
 */

import axios from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

// Get base URL from environment variable
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
const ENABLE_DEBUG_LOGS = import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Adds authentication token to requests
 */
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage (guarded: storage access can throw SecurityError when blocked)
    let token = null;
    try { token = localStorage.getItem('localToken'); } catch { /* storage blocked — proceed unauthenticated */ }

    // Add token to headers if it exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Identify portal-origin requests so the API can enforce portal-specific
    // settings (e.g. the organization "form submission via portal" toggle).
    config.headers['X-Client'] = 'Portal';

    // Add Sentry breadcrumb for API request
    Sentry.addBreadcrumb({
      category: 'api.request',
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        method: config.method?.toUpperCase(),
        url: config.url,
        params: config.params,
        // Don't log sensitive data like passwords
        hasData: !!config.data,
        dataKeys: config.data ? Object.keys(config.data) : [],
      },
    });

    // Log request in development mode
    if (ENABLE_DEBUG_LOGS) {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    // Add Sentry error for request failures
    Sentry.captureException(error, {
      tags: {
        errorType: 'request_interceptor',
      },
    });

    if (ENABLE_DEBUG_LOGS) {
      console.error('❌ Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Response Interceptor
 * Handles responses and errors globally
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Add breadcrumb for successful response
    Sentry.addBreadcrumb({
      category: 'api.response',
      message: `${response.config.method?.toUpperCase()} ${response.config.url}`,
      level: 'info',
      data: {
        url: response.config.url,
        status: response.status,
        statusText: response.statusText,
      },
    });

    // Log successful response in development mode
    if (ENABLE_DEBUG_LOGS) {
      console.log('✅ API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Capture API error details in Sentry
    const errorContext = {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestData: error.config?.data,
      params: error.config?.params,
    };

    // Add breadcrumb for the error
    Sentry.addBreadcrumb({
      category: 'api.error',
      message: `API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      level: 'error',
      data: errorContext,
    });

    // Log error in development mode
    if (ENABLE_DEBUG_LOGS) {
      console.error('❌ API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    }

    // Read-only demo: any write returns 403 { code: 'read_only_demo' }. Surface a
    // friendly toast (deduped) and reject normally — this is not a token problem,
    // so it must not trigger the refresh flow below.
    if (error.response?.status === 403 && error.response?.data?.code === 'read_only_demo') {
      toast.info('This is a read-only demo — changes are disabled.', { toastId: 'read_only_demo' });
    }

    // Handle 401 Unauthorized - Token expired
    // Skip refresh on auth endpoints: a 401 from /Auth/* means bad credentials,
    // not an expired token, so let it propagate to the caller (e.g. LoginPage).
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint = /\/Auth\//i.test(requestUrl);

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        const storedRefreshToken = localStorage.getItem('refreshToken');

        if (storedRefreshToken) {
          const response = await axios.post(
            `${BASE_URL}/api/auth/refresh-token`,
            { refreshToken: storedRefreshToken }, // Send in body, not header
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          const { token, refreshToken: newRefreshToken } = response.data;

          // Update tokens in localStorage
          localStorage.setItem('localToken', token);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Add breadcrumb for successful token refresh
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Token refreshed successfully',
            level: 'info',
          });

          // Update authorization header and retry original request
          originalRequest.headers.Authorization = `Bearer ${token}`;

          // Process queued requests
          processQueue(null, token);
          isRefreshing = false;

          return axiosInstance(originalRequest);
        } else {
          // No refresh token available
          throw new Error('No refresh token available');
        }
      } catch (refreshError) {
        // Refresh failed - clear auth state and redirect to login
        console.error('Token refresh failed:', refreshError);

        // Capture token refresh failure
        Sentry.captureException(refreshError, {
          tags: {
            errorType: 'token_refresh_failed',
          },
          contexts: {
            auth: {
              action: 'token_refresh',
              originalUrl: error.config?.url,
            },
          },
        });

        // Process queued requests with error
        processQueue(refreshError, null);
        isRefreshing = false;

        localStorage.removeItem('localToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');

        // Redirect to login page
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Capture the error in Sentry for non-401, non-404 errors
    // 404s are expected (e.g. no default PDF template) and handled by callers.
    // 403s on demo-pages/* are expected too (non-demo org, or no acting-org context) and already
    // fail-soft in their callers (DemoPageEditorPage renders empty state) — don't report them (AQRATE-PORTAL-12).
    const isExpectedDemoPage403 = error.response?.status === 403 && /\/demo-pages\//.test(error.config?.url || '');
    if ((error.response?.status !== 401 || originalRequest._retry) && error.response?.status !== 404 && !isExpectedDemoPage403) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'api_error',
          httpStatus: error.response?.status,
          endpoint: error.config?.url,
          method: error.config?.method?.toUpperCase(),
        },
        contexts: {
          api: errorContext,
        },
        level: error.response?.status >= 500 ? 'error' : 'warning',
      });
    }

    // Handle specific error codes
    const errorMessage = getErrorMessage(error);

    // Return structured error
    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
      originalError: error,
    });
  }
);

/**
 * Extract user-friendly error message from error response
 * @param {Error} error - Axios error object
 * @returns {string} User-friendly error message
 */
const getErrorMessage = (error) => {
  // Network error
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timeout. Please try again.';
    }
    return 'Network error. Please check your internet connection.';
  }

  const { status, data } = error.response;

  // Extract error message from response
  if (data?.message) return data.message;
  if (data?.error) return data.error;

  // ASP.NET Core validation errors: { errors: { "Field": ["msg1", "msg2"] } }
  if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.entries(data.errors)
      .flatMap(([field, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]).map(m => `${field}: ${m}`));
    if (messages.length > 0) return messages.join('\n');
  }

  if (data?.title) return data.title;

  // Default messages by status code
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Unauthorized. Please log in again.';
    case 403:
      return 'Access forbidden. You do not have permission to perform this action.';
    case 404:
      return 'Resource not found.';
    case 409:
      return 'Conflict. This resource already exists or has been modified.';
    case 422:
      return 'Validation error. Please check your input.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Server error. Please try again later.';
    case 503:
      return 'Service unavailable. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

/**
 * API Error class for better error handling
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Generic API request wrapper with error handling
 * @param {Function} requestFn - Axios request function
 * @returns {Promise} Response data or throws ApiError
 */
export const apiRequest = async (requestFn) => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    throw new ApiError(
      error.message || 'An error occurred',
      error.status,
      error.data
    );
  }
};

export default axiosInstance;
